import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { PromptFieldsFragment } from '@/graphql/gql/graphql';
import type { OmitFunctions } from '@/types/';
import { qsStorage } from '@/utils';
import { createSelectors } from './selector';

export type PromptEditorTab = 'edit' | 'versions';
export type PromptProductFilter = 'all' | 'osce' | 'pace' | 'quiz' | 'content' | 'tutor';

interface PromptSlice {
  page: number;
  search: string;
  typeId: number | null;
  productFilter: PromptProductFilter;
  editorOpen: boolean;
  editorPrompt: PromptFieldsFragment | null;
  editorInitialTab: PromptEditorTab;

  setSearch: (search: string) => void;
  setTypeId: (typeId: number | null) => void;
  setProductFilter: (productFilter: PromptProductFilter) => void;
  setPage: (page: number) => void;
  resetFilters: () => void;
  openPromptEditor: (prompt: PromptFieldsFragment | null, options?: { initialTab?: PromptEditorTab }) => void;
  setEditorPrompt: (prompt: PromptFieldsFragment | null) => void;
  closePromptEditor: () => void;
}

const initialState: OmitFunctions<PromptSlice> = {
  page: 1,
  search: '',
  typeId: null,
  productFilter: 'all',
  editorOpen: false,
  editorPrompt: null,
  editorInitialTab: 'edit',
};

const promptSlice = create<PromptSlice>()(
  persist(
    (set) => ({
      ...initialState,
      setSearch: (search) => {
        set({ search, page: 1 });
      },
      setTypeId: (typeId) => {
        set({ typeId, page: 1 });
      },
      setProductFilter: (productFilter) => {
        set({ productFilter, page: 1 });
      },
      setPage: (page) => {
        set({ page });
      },
      resetFilters: () => {
        set({
          search: initialState.search,
          typeId: initialState.typeId,
          productFilter: initialState.productFilter,
          page: initialState.page,
        });
      },
      openPromptEditor: (prompt, options) => {
        set({
          editorOpen: true,
          editorPrompt: prompt,
          editorInitialTab: options?.initialTab ?? 'edit',
        });
      },
      setEditorPrompt: (prompt) => {
        set({ editorPrompt: prompt });
      },
      closePromptEditor: () => {
        set({ editorOpen: false, editorPrompt: null, editorInitialTab: 'edit' });
      },
    }),
    {
      name: 'prompt-filters',
      storage: createJSONStorage(() => qsStorage),
      partialize: (state) => ({
        search: state.search,
        typeId: state.typeId,
        productFilter: state.productFilter,
        page: state.page,
      }),
    },
  ),
);

export const usePrompt = createSelectors(promptSlice);
